import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { createResourceLocatorString } from '../../../util/routes';
import { Heading } from '../../../components';
import {
  getListingCategoryLabels,
  getListingLocationWithState,
  isVerifiedOwner,
} from '../../../util/listingPageContent';
import { isPremiumListing } from '../../../util/listingPhotosExtras';
import {
  ensureWishlistInitialized,
  selectIsListingFavorited,
  toggleWishlistListing,
} from '../../../ducks/wishlist.duck';
import { IconLocation } from '../ListingIcons';

import css from './ListingPageSections.module.css';

/**
 * Listing title, categories, location, verified badge, share/favorite.
 *
 * @component
 */
const SectionListingHeading = props => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const routes = useRouteConfiguration();
  const { title, publicData, categoryConfiguration, author, listingId, className, rootClassName } =
    props;
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const isFavorited = useSelector(state => selectIsListingFavorited(state, listingId));
  const locationLabel = getListingLocationWithState(publicData);
  const categories = getListingCategoryLabels(publicData, categoryConfiguration);
  // Listing page shows Figma "Verified Owner" (not the dark Premium badge)
  // for verified owners and for premium/boosted listings.
  const showVerifiedOwner =
    isVerifiedOwner(publicData, author) || isPremiumListing(publicData);
  const classes = classNames(rootClassName || css.headingSection, className);

  useEffect(() => {
    dispatch(ensureWishlistInitialized());
  }, [dispatch]);

  const handleShare = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (e) {
        // User cancelled or share failed — ignore
      }
      return;
    }
    if (navigator.clipboard && url) {
      try {
        await navigator.clipboard.writeText(url);
      } catch (e) {
        // Clipboard unavailable — ignore
      }
    }
  };

  const handleFavorite = () => {
    if (!isAuthenticated) {
      history.push(createResourceLocatorString('LoginPage', routes, {}, {}), {
        from: `${location.pathname}${location.search}${location.hash}`,
      });
      return;
    }
    if (listingId) {
      dispatch(toggleWishlistListing(listingId));
    }
  };

  return (
    <header className={classes}>
      <div className={css.headingTopRow}>
        {categories.length ? (
          <div className={css.categoryLabels}>
            {categories.map((cat, index) => (
              <span
                key={cat.id}
                className={classNames(css.categoryLabel, {
                  [css.categoryLabelMuted]: index > 0,
                })}
              >
                {cat.name}
              </span>
            ))}
          </div>
        ) : (
          <div />
        )}
        <div className={css.headingActions}>
          <button
            type="button"
            className={css.iconActionButton}
            onClick={handleShare}
            aria-label={intl.formatMessage({ id: 'ListingPage.shareListing' })}
          >
            <span className={css.iconActionGlyph}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M6.66788 2C4.70364 2.02139 3.60739 2.14619 2.879 2.87459C2 3.75358 2 5.16832 2 7.99773C2 10.8273 2 12.242 2.879 13.121C3.758 14 5.17274 14 8.00221 14C10.8317 14 12.2464 14 13.1254 13.121C13.8539 12.3925 13.9786 11.2959 14 9.33087"
                  stroke="#3F6EF5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
                <path
                  d="M9.33203 2H11.9987C12.9415 2 13.4129 2 13.7058 2.29289C13.9987 2.58579 13.9987 3.05719 13.9987 4V6.66667M13.332 2.66667L7.33203 8.66667"
                  stroke="#3F6EF5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </span>
          </button>
          <button
            type="button"
            className={css.iconActionButton}
            onClick={handleFavorite}
            aria-label={intl.formatMessage({
              id: isFavorited ? 'ListingPage.unfavoriteListing' : 'ListingPage.favoriteListing',
            })}
            aria-pressed={isFavorited}
          >
            <span className={css.iconActionGlyph}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M6.93916 13.312C5.05831 11.9055 1.33203 8.69003 1.33203 5.79646C1.33203 3.88392 2.73554 2.3335 4.66536 2.3335C5.66536 2.3335 6.66536 2.66683 7.9987 4.00016C9.33203 2.66683 10.332 2.3335 11.332 2.3335C13.2618 2.3335 14.6654 3.88392 14.6654 5.79646C14.6654 8.69003 10.9391 11.9055 9.05823 13.312C8.4253 13.7852 7.5721 13.7852 6.93916 13.312Z"
                  stroke="#3F6EF5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill={isFavorited ? '#3F6EF5' : 'none'}
                />
              </svg>
            </span>
          </button>
        </div>
      </div>

      <Heading as="h1" rootClassName={css.listingTitle}>
        {title}
      </Heading>

      <div className={css.headingMeta}>
        {locationLabel ? (
          <span className={css.locationMeta}>
            <span className={css.locationIcon}>
              <IconLocation />
            </span>
            {locationLabel}
          </span>
        ) : null}
        {locationLabel && showVerifiedOwner ? (
          <span className={css.metaDot} aria-hidden="true" />
        ) : null}
        {showVerifiedOwner ? (
          <span className={css.verifiedBadge}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_heading_verified)">
                <path
                  d="M12.6604 12.6667H12.6667M12.6604 12.6667C12.2452 13.0784 11.4929 12.9758 10.9653 12.9758C10.3177 12.9758 10.0058 13.1025 9.54364 13.5647C9.15011 13.9583 8.62251 14.6667 8.00004 14.6667C7.37757 14.6667 6.84997 13.9583 6.45642 13.5647C5.99423 13.1025 5.68237 12.9758 5.03475 12.9758C4.50716 12.9758 3.75483 13.0784 3.3397 12.6667C2.92125 12.2518 3.02423 11.4963 3.02423 10.9653C3.02423 10.2943 2.87748 9.98577 2.39963 9.50791C1.6888 8.79711 1.33339 8.44164 1.33337 8.00004C1.33338 7.55837 1.68879 7.20297 2.39961 6.49215C2.82617 6.06559 3.02423 5.64289 3.02423 5.03475C3.02423 4.50714 2.9217 3.7548 3.33337 3.33967C3.74833 2.92123 4.50377 3.02421 5.03476 3.02421C5.64289 3.02421 6.06559 2.82617 6.49214 2.39962C7.20297 1.68879 7.55837 1.33337 8.00004 1.33337C8.44171 1.33337 8.79711 1.68879 9.50791 2.39962C9.93437 2.82609 10.357 3.02421 10.9653 3.02421C11.4929 3.02421 12.2453 2.92169 12.6604 3.33337C13.0788 3.74833 12.9758 4.50377 12.9758 5.03475C12.9758 5.70576 13.1226 6.01429 13.6004 6.49215C14.3113 7.20297 14.6667 7.55837 14.6667 8.00004C14.6667 8.44164 14.3113 8.79711 13.6004 9.50791C13.1226 9.98577 12.9758 10.2943 12.9758 10.9653C12.9758 11.4963 13.0788 12.2518 12.6604 12.6667Z"
                  stroke="white"
                  strokeWidth="1.2"
                  fill="none"
                />
                <path
                  d="M6 8.59531C6 8.59531 6.8 9.02984 7.2 9.66671C7.2 9.66671 8.4 7.16671 10 6.33337"
                  stroke="white"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </g>
              <defs>
                <clipPath id="clip0_heading_verified">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg>

            <FormattedMessage id="ListingPage.verifiedOwner" />
          </span>
        ) : null}
      </div>
    </header>
  );
};

export default SectionListingHeading;
